import type { Attachment } from '@volt/shared';

/** The slice of the API client the media share flow needs (kept narrow so it is easy to fake). */
export interface ShareMediaApi {
  createJobAttachment(jobId: string, index: number): Promise<{ attachment: Attachment }>;
  sendDirectMessage(
    friendId: string,
    content: string,
    clientRequestId: string,
    replyToMessageId?: string,
    attachmentIds?: string[],
  ): Promise<unknown>;
  sendGroupMessage(
    groupId: string,
    content: string,
    clientRequestId: string,
    replyToMessageId?: string,
    attachmentIds?: string[],
  ): Promise<unknown>;
}

/** State that must survive retries within one open dialog. */
export interface ShareMediaSession {
  attachmentId: string | null;
  /** One request id per destination: re-sending after a network error never posts twice. */
  requestIds: Map<string, string>;
}

export function createShareMediaSession(): ShareMediaSession {
  return { attachmentId: null, requestIds: new Map() };
}

export function destinationKey(dest: { kind: 'friend' | 'group'; id: string }): string {
  return `${dest.kind}:${dest.id}`;
}

/**
 * Stores the result as a chat attachment (once per dialog, however many chats it
 * goes to) and posts it as an attachment-only message to the destination.
 */
export async function sendMediaToDestination(
  api: ShareMediaApi,
  session: ShareMediaSession,
  media: { jobId: string; resultIndex: number },
  dest: { kind: 'friend' | 'group'; id: string },
  newRequestId: () => string = () => crypto.randomUUID(),
): Promise<void> {
  if (!session.attachmentId) {
    const { attachment } = await api.createJobAttachment(media.jobId, media.resultIndex);
    session.attachmentId = attachment.id;
  }
  const key = destinationKey(dest);
  let requestId = session.requestIds.get(key);
  if (!requestId) {
    requestId = newRequestId();
    session.requestIds.set(key, requestId);
  }
  const attachmentIds = [session.attachmentId];
  if (dest.kind === 'friend') {
    await api.sendDirectMessage(dest.id, '', requestId, undefined, attachmentIds);
  } else {
    await api.sendGroupMessage(dest.id, '', requestId, undefined, attachmentIds);
  }
}
